import { route } from 'ziggy-js';
import { resolveShopId } from '@/ShopManagement/resolveShopId.js';
import { TimeClockError } from '@/ShopManagement/timeClockFormatters.js';

/**
 * Time Clock service — the reusable, framework-agnostic data-access layer.
 *
 * It owns HTTP calls to the Time Clock endpoints (via the global axios and Ziggy
 * `route()`) and the Pusher/Echo channel subscriptions. It holds no state and has
 * no Vue/Pinia dependency, so it can be reused directly or driven by the Pinia
 * store (see stores/timeClock.js). Server validation errors are rethrown as
 * TimeClockError so the { code, message, fields } contract survives the network.
 */

const http = () => window.axios;

/**
 * Build a Time Clock endpoint URL for the current shop.
 *
 * The `time-clock.*` routes are the shell-neutral ones. Their
 * `shop-management.time-clock.*` twins sit behind EnabledBayhiveOnShop, which
 * aborts 403 on a host check before reaching its Time Clock exception — so they
 * are unreachable on the Autoflow host. These work in both shells.
 */
export function endpoint(name, params = {}) {
  return route(`time-clock.${name}`, { shop_id: resolveShopId(), ...params });
}

/**
 * Issue a request to a Time Clock endpoint and unwrap the `{ data }` envelope.
 *
 * @param {string} method  http verb
 * @param {string} name    route suffix under `time-clock.`
 * @param {{ params?: object, body?: any }} [options]
 */
export async function request(method, name, { params = {}, body } = {}) {
  try {
    const response = await http()({ method, url: endpoint(name, params), data: body });

    return response.data?.data;
  } catch (error) {
    const data = error?.response?.data;
    if (data && data.code) {
      throw new TimeClockError(data.code, data.message, data.fields || {});
    }
    throw error;
  }
}

export const timeClockService = {
  endpoint,
  request,

  /** The shop the current page/widget belongs to (prop → URL → localStorage). */
  currentShopId: () => resolveShopId(),

  // ---- Reads / mutations ----
  bootstrap: () => request('get', 'bootstrap'),
  clockIn: (payload = {}) => request('post', 'clock-in', { body: payload }),
  clockOut: (payload = {}) => request('post', 'clock-out', { body: payload }),
  startBreak: (breakTypeId) => request('post', 'breaks.start', { body: { breakTypeId } }),
  endBreak: () => request('post', 'breaks.end', { body: {} }),
  saveEntry: (payload) => request('post', 'entries.save', { body: payload }),
  requestCorrection: (payload) => request('post', 'corrections.request', { body: payload }),
  requestMissingClock: (payload) => request('post', 'missing-clock.request', { body: payload }),
  resolveReview: (reviewId, status, proposed = null) =>
    request('post', 'reviews.resolve', {
      params: { review: reviewId },
      body: { status, proposed },
    }),
  managerSetEmployeeClock: (payload) => request('post', 'manager-clock', { body: payload }),
  saveSettings: (settings) => request('put', 'settings.save', { body: settings }),

  // ---- Realtime (Pusher/Echo) ----
  /**
   * Listen for the TimeClockUpdated broadcast on a private channel. The leading
   * dot matches the event's broadcastAs() name exactly, bypassing Echo's default
   * `App.Events` namespace. No-op when Echo is unavailable.
   */
  subscribe(channel, onUpdate) {
    if (typeof window === 'undefined' || !window.Echo) return;
    window.Echo.private(channel).listen('.TimeClockUpdated', onUpdate);
  },

  leave(channel) {
    if (channel && typeof window !== 'undefined' && window.Echo) {
      window.Echo.leave(channel);
    }
  },
};
